import React, { useContext, useState, useEffect, useMemo } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import { useAuthFetch } from "../../../hooks/useAuthFetch";
import { SchedulingCustomerFilter } from "./filters/customerFilter";
import { SchedulingSiteFilter } from "./filters/siteFilter";
import Datepicker from "react-tailwindcss-datepicker";
import { PaperAirplaneIcon } from "@heroicons/react/24/solid";
import { WeekIterator } from "../../../components/button/weekIterator";
import { ScheduleTable } from "./scheduleTable";
import { UserProfile } from "../../../components/profile";
import { ShiftCard } from "../../../components/card/shiftCard";
import { ShiftDetails } from "../shiftTemplates/details";
import { OverviewSheet } from "../../../components/sheet/overviewSheet";
import { isShiftInPast } from "../../../utils/dateTimeUtil";
import { MultiSelect } from "../../../components/menu/multiSelect";
import { DropdownButton } from "../../../components/menu/dropdownButton";
import { WarningModal } from "../../../components/modal/warningModal";
import { Toast } from "../../../components/toast";
import { Link } from "react-router-dom";
import { TableCellsIcon } from "@heroicons/react/24/outline";
import { PageHeading } from "../../../components/pageHeading";

function getWeekDatesArray(startDate) {
  // Create a new date object for the start date
  const start = new Date(startDate);
  start.setHours(0, 0, 0, 0);

  // Get the day of the week (0 = Sunday, 1 = Monday, etc.)
  const day = start.getDay();

  // If it's not Sunday, adjust to the next Sunday
  if (day !== 0) {
    start.setDate(start.getDate() + (7 - day));
  }

  const dates = [];
  // Generate dates for the week starting from Sunday
  for (let i = 0; i < 7; i++) {
    const date = new Date(start);
    date.setDate(start.getDate() + i);
    dates.push(date);
  }

  return dates;
}

function formatTime(date) {
  return date
    .toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h23",
      });
}

function processScheduleData(schedules, weekStart, users) {
  // Build a map of userId to user info
  const userMap = {};
  users.forEach((user) => {
    userMap[user.id] = user;
  });

  // Get all unique week dates using local time
  const weekDates = getWeekDatesArray(weekStart).map((d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  });

  // For each user, build a row with their shifts grouped by date
  const userRows = users.map((user) => {
    const row = {
      firstName: user.first_name,
      lastName: user.last_name,
      imageUrl: user.profile_url,
      userId: user.id,
      hours: 0,
      customerIds: [],
      siteIds: [],
    };

    weekDates.forEach((date) => {
      let shiftDefs = [];
      let allActivities = [];
      schedules.forEach((schedule) => {
        if (Array.isArray(schedule.shift_definitions)) {
          const foundShifts = schedule.shift_definitions.filter(
            (sd) =>
              sd.user_id === user.id &&
              sd.start_time &&
              (() => {
                const shiftDate = new Date(sd.start_time);
                const year = shiftDate.getFullYear();
                const month = String(shiftDate.getMonth() + 1).padStart(2, "0");
                const day = String(shiftDate.getDate()).padStart(2, "0");
                const shiftDateLocal = `${year}-${month}-${day}`;
                return shiftDateLocal === date;
              })(),
          );
          if (foundShifts.length > 0) {
            shiftDefs.push(...foundShifts);
            foundShifts.forEach((shift) => {
              allActivities.push(...(shift.activities || []));
            });
          }
        }
      });
      if (shiftDefs.length > 0) {
        // Sort shifts by start time (earliest first)
        shiftDefs.sort((a, b) => {
          const startTimeA = new Date(a.start_time);
          const startTimeB = new Date(b.start_time);
          return startTimeA - startTimeB;
        });

        // Calculate total hours for all shifts
        let totalShiftHours = 0;
        shiftDefs.forEach((shiftDef) => {
          const startTime = new Date(shiftDef.start_time);
          const endTime = new Date(shiftDef.end_time);
          const shiftHours = (endTime - startTime) / (1000 * 60 * 60);
          totalShiftHours += shiftHours;
        });

        // Aggregate customerIds and siteIds for all shifts
        const customerIds = Array.from(
          new Set(
            (allActivities || []).flatMap((a) =>
              a.customer ? [a.customer.id] : [],
            ),
          ),
        );
        const siteIds = Array.from(
          new Set(
            (allActivities || []).flatMap((a) =>
              a.site ? [a.site.id] : [],
            ),
          ),
        );

        // Add total shift hours to total
        row.hours += totalShiftHours;
        row.customerIds = Array.from(
          new Set([...row.customerIds, ...customerIds]),
        );
        row.siteIds = Array.from(
          new Set([...row.siteIds, ...siteIds]),
        );

        // Store all shifts in the same structure
        const shiftData = {
          shiftDefs: shiftDefs,
          shiftName: shiftDefs[0].name, // Keep first shift name for backward compatibility
          shiftTime:
            shiftDefs[0].start_time && shiftDefs[0].end_time
              ? `${formatTime(new Date(shiftDefs[0].start_time))}-${formatTime(
                  new Date(shiftDefs[0].end_time),
                )}`
              : "",
          shiftId: shiftDefs[0].id, // Keep first shift ID for backward compatibility
          activityIds: (allActivities || []).map((a) => a.id),
          status: shiftDefs[0].status,
          instructions: shiftDefs[0].instructions,
          durationMinutes: Math.round(totalShiftHours * 60),
          customerIds,
          siteIds,
        };

        row[date] = shiftData;
      } else {
        row[date] = null;
      }
    });
    return row;
  });
  return userRows;
}

function getMostRecentSunday() {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const day = today.getDay();
  if (day !== 0) {
    today.setDate(today.getDate() - day);
  }
  // Use local time formatting to avoid timezone issues
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const dayOfMonth = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${dayOfMonth}`;
}

// Generate previous weeks for copy functionality
function generatePreviousWeeks(currentWeekStart, maxWeeks = 12) {
  const weeks = [];
  const currentDate = new Date(currentWeekStart);

  for (let i = 1; i <= maxWeeks; i++) {
    const weekDate = new Date(currentDate);
    weekDate.setDate(currentDate.getDate() - i * 7);

    // Use toISOString().split('T')[0] to avoid timezone issues
    const weekStart = weekDate.toISOString().split("T")[0];

    // Format for display - end date is Saturday (6 days after Sunday)
    const endDate = new Date(weekDate);
    endDate.setDate(weekDate.getDate() + 6);
    const weekEnd = endDate.toISOString().split("T")[0];

    const weekName = `${weekStart} to ${weekEnd}`;

    weeks.push({
      id: weekStart,
      name: weekName,
      weekStart: weekStart,
    });
  }

  return weeks;
}

async function fetchWeeksMetadata(orgId, weeks, authFetch) {
  try {
    const response = await authFetch(
      `${process.env.REACT_APP_API_URL}/v1/schedules/weeks-meta`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          org_id: orgId,
          weeks: weeks,
        }),
      },
    );

    if (!response.ok) {
      throw new Error("Failed to fetch weeks metadata");
    }

    const data = await response.json();
    return data.meta;
  } catch (error) {
    console.error("Error fetching weeks metadata:", error);
    // Return default metadata for all weeks
    return weeks.map((weekStart) => ({
      weekStart,
      numGuards: 0,
      totalHours: 0,
      totalShifts: 0,
    }));
  }
}

async function fetchCurrentWeekMetadata(orgId, weekStart, authFetch) {
  try {
    const metadata = await fetchWeeksMetadata(orgId, [weekStart], authFetch);
    return metadata[0] || { numGuards: 0, totalHours: 0, totalShifts: 0 };
  } catch (error) {
    console.error("Error fetching current week metadata:", error);
    return { numGuards: 0, totalHours: 0, totalShifts: 0 };
  }
}

function Schedules() {
  const { authContext } = useContext(AuthContext);
  const orgId = authContext.organization.orgId;
  const authFetch = useAuthFetch();
  const hasSchedulesAccess =
    authContext.organization.planFeatures?.Schedules !== false;

  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [selectedSites, setSelectedSites] = useState([]);
  const [sitesOptions, setSitesOptions] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [isShiftDetailsOpen, setIsShiftDetailsOpen] = useState(false);
  const [selectedShiftData, setSelectedShiftData] = useState(null);
  const [isAddMode, setIsAddMode] = useState(false);
  const [weekScheduleData, setWeekScheduleData] = useState({});
  const [isLoadingWeekData, setIsLoadingWeekData] = useState(false);
  const [isCopyWarningOpen, setIsCopyWarningOpen] = useState(false);
  const [pendingCopyData, setPendingCopyData] = useState(null);
  const [isCopying, setIsCopying] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const [currentWeekMetadata, setCurrentWeekMetadata] = useState({
    numGuards: 0,
    totalHours: 0,
  });

  // Initialize with the most recent Sunday (current week)
  const [dateRange, setDateRange] = useState(() => {
    const sunday = getMostRecentSunday();
    return {
      startDate: sunday,
      endDate: sunday,
    };
  });

  // Determine if the current week is in the past or current week
  const isCurrentWeekInPastOrCurrent = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const currentWeekStart = new Date(dateRange.startDate);
    currentWeekStart.setHours(0, 0, 0, 0);

    // If current week start is today or in the past, disable the copy menu
    return currentWeekStart <= today;
  }, [dateRange.startDate]);

  /// Calendar ///
  const handleselectedDate = (selectedDateRange) => {
    const selectedDate = new Date(selectedDateRange.startDate);
    selectedDate.setHours(0, 0, 0, 0);

    // Get the day of the week (0 = Sunday, 1 = Monday, etc.)
    const day = selectedDate.getDay();

    // If it's not Sunday, adjust to the previous Sunday
    if (day !== 0) {
      selectedDate.setDate(selectedDate.getDate() - day);
    }

    // Use local time formatting to avoid timezone issues
    const year = selectedDate.getFullYear();
    const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
    const dayOfMonth = String(selectedDate.getDate()).padStart(2, "0");
    const sundayDate = `${year}-${month}-${dayOfMonth}`;

    setDateRange({
      startDate: sundayDate,
      endDate: sundayDate,
    });
  };

  const handlePreviousWeek = () => {
    const current = new Date(dateRange.startDate);
    current.setDate(current.getDate() - 7);
    const newDate = current.toISOString().split("T")[0];
    setDateRange({
      startDate: newDate,
      endDate: newDate,
    });
  };

  const handleNextWeek = () => {
    const current = new Date(dateRange.startDate);
    current.setDate(current.getDate() + 7);
    const newDate = current.toISOString().split("T")[0];
    setDateRange({
      startDate: newDate,
      endDate: newDate,
    });
  };

  const openShiftDetails = (shiftData, isAdd = false) => {
    setSelectedShiftData(shiftData);
    setIsAddMode(isAdd);
    setIsShiftDetailsOpen(true);
  };

  const closeShiftDetails = () => {
    setIsShiftDetailsOpen(false);
    setSelectedShiftData(null);
    setIsAddMode(false);
  };

  const handleResponse = (message, type) => {
    setToast({ show: true, message, type });
  };

  const hideToast = () => {
    setToast({ show: false, message: "", type: "" });
  };

  const handleCopyFromWeek = async (weekStart) => {
    if (!weekStart) return;

    try {
      // First, check if source week has any shifts
      const sourceWeekData = weekScheduleData[weekStart];
      if (
        !sourceWeekData ||
        sourceWeekData.numGuards === 0 ||
        sourceWeekData.totalHours === 0
      ) {
        handleResponse("There are no shifts to copy from this week.", "alert");
        return;
      }

      // Check if target week has existing shifts
      const targetWeekHasShifts = schedules.some((row) => row.hours > 0);

      if (targetWeekHasShifts) {
        // Show warning modal
        setPendingCopyData({ weekStart });
        setIsCopyWarningOpen(true);
        return;
      }

      // No existing shifts, proceed with copy
      await performCopy(weekStart);
    } catch (error) {
      console.error("Error in copy process:", error);
      handleResponse("Failed to copy schedule. Please try again.", "danger");
    }
  };

  const performCopy = async (weekStart) => {
    if (!weekStart) return;

    setIsCopying(true);
    try {
      console.log("Frontend: Starting schedule copy", {
        source_week_start: weekStart,
        target_week_start: dateRange.startDate,
        org_id: orgId,
      });

      // Use the new dedicated copy endpoint
      const copyResponse = await authFetch(
        `${process.env.REACT_APP_API_URL}/v1/schedules/copy`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            source_week_start: weekStart,
            target_week_start: dateRange.startDate,
            org_id: orgId,
            options: {
              overwrite_existing: true,
              copy_activities: true,
            },
          }),
        },
      );

      if (!copyResponse.ok) {
        const errorData = await copyResponse.json();
        throw new Error(errorData.error || "Failed to copy schedule");
      }

      const result = await copyResponse.json();

      // Refresh the current week data
      await fetchData();

      // Show success message with details
      const message = "Schedule copied successfully!";
      handleResponse(message, "success");
    } catch (error) {
      console.error("Error copying from week:", error);
      handleResponse(`Failed to copy schedule: ${error.message}`, "danger");
    } finally {
      setIsCopying(false);
    }
  };

  const handleCopyConfirm = async () => {
    setIsCopyWarningOpen(false);
    if (pendingCopyData) {
      await performCopy(pendingCopyData.weekStart);
      await fetchCurrentWeekData(); // Refresh current week metadata after copy
      setPendingCopyData(null);
    }
  };

  const handleCopyCancel = () => {
    setIsCopyWarningOpen(false);
    setPendingCopyData(null);
  };

  /// Table data ///
  // Memoize the columns to prevent unnecessary recalculations
  const columns = useMemo(() => {
    const weekDates = getWeekDatesArray(dateRange.startDate);

    return [
      {
        Header: () => <div></div>,
        accessor: "name",
        Cell: ({ row }) => {
          const { firstName, lastName, imageUrl, hours } = row.original;
          return (
            <div className="flex items-center space-x-1">
              <div className="py-1 pl-1">
                <UserProfile
                  imageUrl={imageUrl}
                  size="small"
                  firstName={firstName}
                  lastName={lastName}
                  useInitials={true}
                />
              </div>
              <div className="flex flex-col">
                <span className="text-xsm font-semi-bold text-gray-800 dark:text-gray-100 -ml-2">
                  {firstName} {lastName}
                </span>
                <span className="text-xs text-gray-600 dark:text-gray-400 -ml-2">
                  {hours} hours
                </span>
              </div>
            </div>
          );
        },
      },
      ...weekDates.map((date) => ({
        Header: date.toLocaleDateString("en-US", {
          weekday: "short",
          month: "short",
          day: "numeric",
        }),
        accessor: date.toISOString().split("T")[0],
        className: "p-0",
        Cell: ({ value, row, column }) => {
          const dateKey = column.id;
          const isCardSelected =
            isShiftDetailsOpen &&
            selectedShiftData?.date === dateKey &&
            (isAddMode
              ? selectedShiftData?.firstName === row.original.firstName &&
                selectedShiftData?.lastName === row.original.lastName
              : selectedShiftData?.user?.firstName === row.original.firstName &&
                selectedShiftData?.user?.lastName === row.original.lastName);

          return (
            <div className="flex w-full h-full min-h-[40px] cursor-pointer bg-transparent items-stretch">
              {value && value.shiftName && value.shiftTime ? (
                value.shiftDefs && value.shiftDefs.length > 1 ? (
                  <div className="w-11/12 flex flex-row space-x-1 m-1">
                    {value.shiftDefs.map((shiftDef) => {
                      // Check if shift is in the past using local time
                      const shiftDateObj = new Date(shiftDef.start_time);
                      const shiftDate = (() => {
                        const year = shiftDateObj.getFullYear();
                        const month = String(
                          shiftDateObj.getMonth() + 1,
                        ).padStart(2, "0");
                        const day = String(shiftDateObj.getDate()).padStart(
                          2,
                          "0",
                        );
                        return `${year}-${month}-${day}`;
                      })();
                      const shiftEndTime = new Date(shiftDef.end_time)
                        .toTimeString()
                        .split(" ")[0]
                        .substring(0, 5);
                      const shiftInPast = isShiftInPast(
                        shiftDate,
                        shiftEndTime,
                      );

                      return (
                        <div key={shiftDef.id} className="flex-1 min-w-0">
                          <ShiftCard
                            title={shiftDef.name}
                            post={shiftDef.post?.name}
                            time={
                              shiftDef.start_time && shiftDef.end_time
                                ? `${formatTime(
                                    new Date(shiftDef.start_time),
                                  )}-${formatTime(new Date(shiftDef.end_time))}`
                                : ""
                            }
                            isSelected={
                              isCardSelected &&
                              selectedShiftData?.id === shiftDef.id
                            }
                            canChange={!shiftInPast}
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent cell click
                              const shiftData = {
                                shiftName: shiftDef.name,
                                shiftTime:
                                  shiftDef.start_time && shiftDef.end_time
                                    ? `${formatTime(
                                        new Date(shiftDef.start_time),
                                      )}-${formatTime(
                                        new Date(shiftDef.end_time),
                                      )}`
                                    : "",
                                shiftId: shiftDef.id,
                                activityIds: (shiftDef.activities || []).map(
                                  (a) => a.id,
                                ),
                                status: shiftDef.status,
                                instructions: shiftDef.instructions,
                                customerIds: Array.from(
                                  new Set(
                                    (shiftDef.activities || []).flatMap((a) =>
                                      a.customer ? [a.customer.id] : [],
                                    ),
                                  ),
                                ),
                                siteIds: Array.from(
                                  new Set(
                                    (shiftDef.activities || []).flatMap((a) =>
                                      a.site ? [a.site.id] : [],
                                    ),
                                  ),
                                ),
                                date: dateKey,
                                user: row.original,
                                id: shiftDef.id,
                              };
                              openShiftDetails(shiftData, false);
                            }}
                          />
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="w-11/12 m-1">
                    {(() => {
                      // Check if shift is in the past
                      const shiftDate = dateKey;
                      const shiftEndTime = value.shiftDefs[0].end_time
                        ? new Date(value.shiftDefs[0].end_time)
                            .toTimeString()
                            .split(" ")[0]
                            .substring(0, 5)
                        : "17:00"; // fallback
                      const shiftInPast = isShiftInPast(
                        shiftDate,
                        shiftEndTime,
                      );

                      return (
                        <ShiftCard
                          title={value.shiftName}
                          time={value.shiftTime}
                          isSelected={isCardSelected}
                          canChange={!shiftInPast}
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent cell click
                            const shiftData = {
                              ...value,
                              date: dateKey,
                              user: row.original,
                              id: value.shiftId,
                            };
                            openShiftDetails(shiftData, false);
                          }}
                        />
                      );
                    })()}
                  </div>
                )
              ) : null}
              {/* Always render the right-side clickable area for add shift */}
              <div
                className={`flex-1 ${(() => {
                  // Check if this date is in the past using local time comparison
                  const today = new Date();
                  const todayYear = today.getFullYear();
                  const todayMonth = today.getMonth();
                  const todayDay = today.getDate();
                  const todayLocal = new Date(todayYear, todayMonth, todayDay);

                  // Parse the dateKey to get local date
                  const [year, month, day] = dateKey.split("-").map(Number);
                  const dateKeyLocal = new Date(year, month - 1, day);

                  const isPastDate = dateKeyLocal < todayLocal;

                  // Cell background/hover is applied to the <td> in scheduleTable
                  // (so it fills the full cell height); here we only set the cursor.
                  return isPastDate ? "cursor-not-allowed" : "cursor-pointer";
                })()}`}
                onClick={() => {
                  // Check if this date is in the past using local time comparison
                  const today = new Date();
                  const todayYear = today.getFullYear();
                  const todayMonth = today.getMonth();
                  const todayDay = today.getDate();
                  const todayLocal = new Date(todayYear, todayMonth, todayDay);

                  // Parse the dateKey to get local date
                  const [year, month, day] = dateKey.split("-").map(Number);
                  const dateKeyLocal = new Date(year, month - 1, day);

                  const isPastDate = dateKeyLocal < todayLocal;

                  if (!isPastDate) {
                    const shiftData = {
                      date: dateKey,
                      ...row.original, // Spread the user properties directly
                    };
                    openShiftDetails(shiftData, true);
                  }
                }}
              />
            </div>
          );
        },
      })),
    ];
  }, [dateRange.startDate, isShiftDetailsOpen, selectedShiftData]);

  // Separate effect for table data
  const fetchData = async () => {
    try {
      setIsLoading(true);

      // Add a 500ms delay
      await new Promise((resolve) => setTimeout(resolve, 150));

      // Fetch users
      const usersUrl = new URL(
        `${process.env.REACT_APP_API_URL}/v1/users?org_id=${orgId}`,
      );
      const usersResponse = await authFetch(usersUrl);
      if (!usersResponse.ok) throw new Error("Failed to fetch users");
      const usersData = await usersResponse.json();

      // Get the users array from the response
      const usersArray = usersData.users || [];

      // Filter users to only include guards
      const guards = usersArray.filter((user) => {
        // Check if user has guard:access in their roles array
        return user.roles && user.roles.includes("guard:access");
      });
      setUsers(guards);

      // Fetch schedules
      const schedulesUrl = new URL(
        `${process.env.REACT_APP_API_URL}/v1/schedules`,
      );
      schedulesUrl.searchParams.append("org_id", orgId);
      schedulesUrl.searchParams.append("week_start", dateRange.startDate);

      const schedulesResponse = await authFetch(schedulesUrl);
      if (!schedulesResponse.ok) throw new Error("Failed to fetch schedules");
      const schedulesData = await schedulesResponse.json();

      // Process schedules and combine with users (guards)
      const scheduleRows = processScheduleData(
        schedulesData,
        dateRange.startDate,
        guards,
      );

      setSchedules(scheduleRows);
    } catch (error) {
      console.error("Error fetching data:", error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch current week metadata for overview sheet
  const fetchCurrentWeekData = async () => {
    try {
      const metadata = await fetchCurrentWeekMetadata(
        orgId,
        dateRange.startDate,
        authFetch,
      );
      setCurrentWeekMetadata(metadata);
    } catch (error) {
      console.error("Error fetching current week metadata:", error);
      setCurrentWeekMetadata({ numGuards: 0, totalHours: 0 });
    }
  };

  useEffect(() => {
    fetchData();
    fetchCurrentWeekData();
  }, [dateRange.startDate]);

  // Load week data on-demand when dropdown opens
  const loadWeekDataOnDemand = async () => {
    if (isCurrentWeekInPastOrCurrent || isLoadingWeekData) return; // Don't fetch if disabled or already loading

    setIsLoadingWeekData(true);
    try {
      const weeks = generatePreviousWeeks(dateRange.startDate, 12);
      const weekStartDates = weeks.map((week) => week.weekStart);

      // Fetch metadata for all weeks in one request
      const metadata = await fetchWeeksMetadata(
        orgId,
        weekStartDates,
        authFetch,
      );

      // Create a map of week data for the dropdown
      const weekDataMap = {};
      metadata.forEach((weekMeta, index) => {
        weekDataMap[weeks[index].id] = {
          numGuards: weekMeta.numGuards,
          totalHours: weekMeta.totalHours,
          totalShifts: weekMeta.totalShifts,
        };
      });

      setWeekScheduleData(weekDataMap);
    } catch (error) {
      console.error("Error fetching week schedule data:", error);
    } finally {
      setIsLoadingWeekData(false);
    }
  };

  const handleCustomerUpdate = (id) => {
    const newSelected = selectedCustomers.includes(id)
      ? selectedCustomers.filter((i) => i !== id)
      : [...selectedCustomers, id];

    // Remove sites not associated with selected customers
    const validSiteIds = sitesOptions
      .filter((loc) => newSelected.includes(loc.customerId))
      .map((loc) => loc.id);

    setSelectedCustomers(newSelected);
    setSelectedSites((prev) =>
      prev.filter((id) => validSiteIds.includes(id)),
    );
  };

  const filterTableDataByCustomerAndSite = (data) => {
    // If no filters are selected, show all data
    if (selectedCustomers.length === 0 && selectedSites.length === 0) {
      return data;
    }

    // Always return all guards, but filter their shift data
    return data.map((row) => {
      // Create a copy of the row to avoid mutating the original
      const filteredRow = { ...row };

      // Get all week dates to check each day
      const weekDates = getWeekDatesArray(dateRange.startDate).map((d) => {
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      });

      // Filter each day's shift data
      weekDates.forEach((date) => {
        if (filteredRow[date]) {
          const shiftData = filteredRow[date];

          // Check if this shift matches the filters
          const matchesCustomer =
            selectedCustomers.length === 0 ||
            (Array.isArray(shiftData.customerIds) &&
              shiftData.customerIds.some((id) =>
                selectedCustomers.includes(id),
              ));
          const matchesSite =
            selectedSites.length === 0 ||
            (Array.isArray(shiftData.siteIds) &&
              shiftData.siteIds.some((id) =>
                selectedSites.includes(id),
              ));

          // If no match, clear the shift data for this day
          if (!matchesCustomer || !matchesSite) {
            filteredRow[date] = null;
          }
        }
      });

      // Recalculate totals for this guard
      let totalHours = 0;
      let allCustomerIds = [];
      let allSiteIds = [];

      weekDates.forEach((date) => {
        if (filteredRow[date]) {
          totalHours += filteredRow[date].durationMinutes / 60;
          allCustomerIds = [
            ...allCustomerIds,
            ...(filteredRow[date].customerIds || []),
          ];
          allSiteIds = [
            ...allSiteIds,
            ...(filteredRow[date].siteIds || []),
          ];
        }
      });

      // Update the row totals
      filteredRow.hours = totalHours;
      filteredRow.customerIds = Array.from(new Set(allCustomerIds));
      filteredRow.siteIds = Array.from(new Set(allSiteIds));

      return filteredRow;
    });
  };

  // Use backend metadata for overview values
  const { numGuards, totalHours } = currentWeekMetadata;

  // Show upgrade message if user doesn't have access to schedules
  if (!hasSchedulesAccess) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="w-full px-4 sm:px-6 lg:px-6">
          <div className="flex flex-col items-center justify-center">
            <TableCellsIcon className="h-10 w-10 text-gray-300 dark:text-gray-600 mb-6" />
            <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">
              Sorry! Scheduling isn't available on the starter plan
            </h2>
            <p className="text-gray-600 dark:text-gray-300 mb-4 text-center max-w-lg">
              Upgrade to the professional plan to create and manage schedules
            </p>
            <p className="text-gray-600 text-center max-w-lg">
              <Link
                to="/billing/subscription"
                className="text-brand-primary hover:text-brand-200 hover:border-b-2 hover:border-brand-primary transition-colors"
              >
                View plans
              </Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <main className="py-6">
        <div className="px-4 sm:px-6 lg:px-6">
          <PageHeading title="Schedules" />
          <div className="flex items-center mb-4">
            <div className="flex-1">
              <div className="inline pl-1 mr-1">
                <SchedulingCustomerFilter
                  selected={selectedCustomers}
                  onUpdate={handleCustomerUpdate}
                  onClear={() => setSelectedCustomers([])}
                />
              </div>
              <div className="pl-1 inline">
                <SchedulingSiteFilter
                  selected={selectedSites}
                  onUpdate={(id) =>
                    setSelectedSites((prev) =>
                      prev.includes(id)
                        ? prev.filter((i) => i !== id)
                        : [...prev, id],
                    )
                  }
                  onClear={() => setSelectedSites([])}
                  customerIds={selectedCustomers}
                  setSitesOptions={setSitesOptions}
                />
              </div>
            </div>

            <div className="flex justify-end">
              <div className="pr-2 flex items-center mr-6 relative">
                <div className="absolute right-0 top-1/2 transform -translate-y-1/2 h-1/2 w-px bg-gray-300 dark:bg-gray-600" />
                <div className="inline mr-2">
                  <WeekIterator
                    onPrevious={handlePreviousWeek}
                    onNext={handleNextWeek}
                  />
                </div>
                <div className="pl-1 mr-4">
                  <Datepicker
                    startFrom={new Date("2025-06-08")}
                    configs={{}}
                    inputClassName={
                      "h-9 w-36 inline-flex rounded-md px-3 -ml-1 items-center text-sm leading-none tracking-normal font-normal text-gray-600 dark:text-gray-200 placeholder-gray-500 dark:placeholder-gray-400 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-white/[0.06] bg-gray-50 dark:bg-gray-800 border-0 pr-10 focus:outline-none"
                    }
                    popupClassName="z-[100]"
                    asSingle={true}
                    value={dateRange}
                    onChange={handleselectedDate}
                  />
                </div>
              </div>
              <DropdownButton
                label={isCopying ? "Copying..." : "Copy"}
                options={generatePreviousWeeks(dateRange.startDate, 12)}
                onOptionSelect={(selectedWeek) => {
                  handleCopyFromWeek(selectedWeek.weekStart);
                }}
                disabled={isCurrentWeekInPastOrCurrent || isCopying}
                fullWidth={false}
                scheduleData={weekScheduleData}
                isLoadingScheduleData={isLoadingWeekData}
                onOpen={loadWeekDataOnDemand}
              />
            </div>
          </div>
          <div>
            {isLoading ? (
              <div className="mt-4">
                <ScheduleTable
                  data={[]}
                  columns={columns}
                  isLoading={isLoading}
                  showHeader={true}
                  skeletonRows={5}
                  onCellAddShift={openShiftDetails}
                />
              </div>
            ) : (
              <div className="mt-4">
                <ScheduleTable
                  data={filterTableDataByCustomerAndSite(schedules)}
                  columns={columns}
                  onCellAddShift={openShiftDetails}
                />
              </div>
            )}
            <ShiftDetails
              isOpen={isShiftDetailsOpen}
              toggleOpen={closeShiftDetails}
              selectedShiftId={selectedShiftData?.id || null}
              isAddMode={isAddMode}
              selectedDate={
                isAddMode ? selectedShiftData?.date : dateRange.startDate
              }
              selectedUserId={isAddMode ? selectedShiftData?.userId : null}
              onSuccess={async () => {
                // Wait for the data to be fetched before closing the modal
                await fetchData();
                await fetchCurrentWeekData();
                closeShiftDetails();
              }}
            />
            {!isShiftDetailsOpen && (
              <div className="flex justify-center w-full mt-8 mb-4">
                <OverviewSheet numGuards={numGuards} totalHours={totalHours} />
              </div>
            )}
          </div>
        </div>
      </main>
      <WarningModal
        isOpen={isCopyWarningOpen}
        onClose={handleCopyCancel}
        onConfirm={handleCopyConfirm}
        sourceWeek={pendingCopyData?.weekStart || ""}
        targetWeek={dateRange.startDate}
      />
      <Toast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onHide={hideToast}
      />
    </div>
  );
}

export { Schedules };
